import { useState } from "react";
import { useForm } from "react-hook-form";
import * as devicesApi from "../../api/devices";
import {
  TipoPossessoDevice,
  TIPO_POSSESSO_LABELS,
  TipoDispositivoDevice,
  TIPI_DISPOSITIVO,
  TIPO_DISPOSITIVO_LABELS,
} from "../../api/devices";
import { SedeSuggestion } from "../../api/anagrafica";
import { TipoDispositivo } from "../../types";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SlideOver } from "../../components/ui/SlideOver";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";

/**
 * Censimento di un dispositivo, INDIPENDENTE da installazione e attivazione.
 *
 * ## Perché esiste
 *
 * Il device non nasce sempre da un'installazione. Il caso più frequente è il
 * contrario: il pezzo è già installato fisicamente e a noi tocca solo
 * l'Attivazione del servizio — non c'è nessuna Installazione da salvare, ma il
 * dispositivo va censito comunque prima, o l'Attivazione non ha niente a cui
 * agganciarsi. Finora questo censimento non si poteva fare da nessuna pagina:
 * la rotta `POST /api/devices` c'era da sempre, e nessuna schermata la
 * chiamava.
 *
 * ## Che cosa scrive
 *
 * Il pezzo, e — se si indica un cliente — la relazione device↔cliente↔sede.
 * La relazione non è un dettaglio: la scheda della sede conta i dispositivi da
 * lì (`device_cliente` con `data_fine IS NULL`), non da `devices.sede_id`, e
 * senza quella riga la sede continuerebbe a dire "DISPOSITIVI: 0" con il pezzo
 * regolarmente censito.
 *
 * ## Un solo campo obbligatorio
 *
 * La matricola, come nella rotta. Tutto il resto può restare vuoto di
 * proposito: un device censito senza modello vale più di un device non
 * censito, e riempire il possesso con un valore di comodo produrrebbe un dato
 * indistinguibile da uno verificato. Vale anche per "installato da Evo", che
 * ha tre risposte — Sì / No / non so — e non due.
 */

type FormValues = {
  matricola: string;
  modello: string;
  dispositivo_catalogo_id: string;
  tipo_dispositivo: TipoDispositivoDevice | "";
  tipo_possesso: TipoPossessoDevice | "";
  /** "" = non noto, ed è diverso da "no". Vedi sotto. */
  installato_da_evo: "" | "si" | "no";
  data_installazione: string;
  note: string;
};

const VALORI_INIZIALI: FormValues = {
  matricola: "",
  modello: "",
  dispositivo_catalogo_id: "",
  tipo_dispositivo: "",
  tipo_possesso: "",
  installato_da_evo: "",
  data_installazione: "",
  note: "",
};

export function DeviceNuovo({
  open,
  onClose,
  onCreato,
}: {
  open: boolean;
  onClose: () => void;
  /** Riceve l'id del pezzo appena censito: chi chiama decide dove portare l'utente. */
  onCreato: (deviceId: string) => void;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues: VALORI_INIZIALI });

  // Sede e cliente stanno fuori dal form perché non sono campi del device ma
  // della RELAZIONE: il cliente arriva dalla sede scelta, non si digita.
  const [sedeTesto, setSedeTesto] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [clienteNome, setClienteNome] = useState("");

  function chiudi() {
    reset(VALORI_INIZIALI);
    setSedeTesto("");
    setSedeId("");
    setClienteId("");
    setClienteNome("");
    onClose();
  }

  function handleSedeSelezionata(sede: SedeSuggestion) {
    setSedeId(sede.id);
    // L'azienda madre arriva insieme alla sede: la relazione vuole il cliente,
    // e farlo scegliere a parte vorrebbe dire poter scegliere una coppia
    // incoerente — che la rotta rifiuterebbe con un errore da interpretare.
    setClienteId(sede.cliente_id);
    setClienteNome(sede.cliente_nome);
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      const { record } = await devicesApi.createDevice({
        matricola: values.matricola,
        dispositivo_catalogo_id: values.dispositivo_catalogo_id || null,
        // Il testo del modello si conserva anche quando il catalogo ha
        // agganciato: è il dato grezzo da cui quella chiave si ricava.
        modello_raw: values.modello.trim() || null,
        tipo_dispositivo: values.tipo_dispositivo || null,
        tipo_possesso: values.tipo_possesso || null,
        // Tre valori, non due: "" resta null, cioè "non dichiarato". Un
        // `false` di default direbbe "non l'ha installato Evo" senza che
        // nessuno l'abbia verificato, e quel campo governa la creazione
        // automatica della riga di installazione.
        installato_da_evo: values.installato_da_evo === "" ? null : values.installato_da_evo === "si",
        sede_id: sedeId || null,
        data_installazione: values.data_installazione || null,
        note: values.note.trim() || null,
      });

      // La relazione col cliente è una seconda chiamata perché è una seconda
      // risorsa (`/api/device-cliente`): il device esiste comunque anche se
      // questa fallisce, e il messaggio deve dire quale delle due è andata.
      if (clienteId) {
        try {
          await devicesApi.creaDeviceCliente({
            device_id: record.id,
            cliente_id: clienteId,
            sede_id: sedeId || null,
          });
        } catch (err) {
          showToast(
            `Dispositivo ${record.matricola} censito, ma il collegamento a ${clienteNome} non è riuscito: ` +
              getErrorMessage(err, "errore sconosciuto") +
              " Puoi collegarlo dalla sua scheda.",
            "error"
          );
          onCreato(record.id);
          chiudi();
          return;
        }
      }

      showToast(
        clienteId
          ? `Dispositivo ${record.matricola} censito e collegato a ${clienteNome}.`
          : `Dispositivo ${record.matricola} censito.`,
        "success"
      );
      onCreato(record.id);
      chiudi();
    } catch (err) {
      // Il 409 della matricola già censita porta con sé l'id del device che la
      // occupa: il messaggio della rotta dice già quale, e riportarlo tale e
      // quale è più utile di un "impossibile salvare" generico.
      showToast(getErrorMessage(err, "Impossibile censire il dispositivo."), "error");
    }
  });

  return (
    <SlideOver
      open={open}
      onClose={chiudi}
      title="Nuovo dispositivo"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={chiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-device-nuovo" disabled={isSubmitting}>
            {isSubmitting ? "Salvataggio…" : "Censisci dispositivo"}
          </Button>
        </div>
      }
    >
      <form id="form-device-nuovo" onSubmit={onSubmit} className="flex flex-col gap-4">
        <Input
          label="Matricola"
          required
          hint="L'identificativo stampigliato sulla macchina. Non è modificabile dopo: tutto lo storico ci punta."
          placeholder="Es. UB120004"
          {...register("matricola", { required: true })}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Tipo dispositivo</label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("tipo_dispositivo")}
          >
            <option value="">— Non classificato —</option>
            {TIPI_DISPOSITIVO.map((t) => (
              <option key={t} value={t}>
                {TIPO_DISPOSITIVO_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <DispositivoAutocomplete
          label="Modello (Catalogo Dispositivi)"
          // Il Catalogo non conosce `Concentratore`: il suo filtro accetta
          // l'unione piu' stretta di `types/index.ts`. Invece di un cast che
          // direbbe il falso, su quel tipo si passa "" — nessun filtro, tutti i
          // modelli — che e' anche la lettura giusta: un accessorio non ha un
          // modello a catalogo da restringere.
          tipoDispositivo={
            watch("tipo_dispositivo") === "Concentratore" ? "" : (watch("tipo_dispositivo") as TipoDispositivo | "")
          }
          value={watch("modello")}
          onChange={(v) => {
            setValue("modello", v);
            // Chi modifica il testo dopo aver scelto non deve tenersi la
            // chiave vecchia: collegherebbe il pezzo a un modello diverso da
            // quello scritto. Stessa regola degli altri autocomplete.
            setValue("dispositivo_catalogo_id", "");
          }}
          onSelect={(d) => {
            setValue("modello", `${d.marca} ${d.modello}`);
            setValue("dispositivo_catalogo_id", d.id);
          }}
        />

        <SedeAutocomplete
          label="Sede"
          value={sedeTesto}
          onChange={setSedeTesto}
          sedeId={sedeId}
          onSedeIdChange={setSedeId}
          onSelect={handleSedeSelezionata}
          soloSelezione
        />
        {clienteId && (
          <p className="-mt-2 text-xs text-ink/60">
            Verrà collegato a <strong>{clienteNome}</strong>. È questo collegamento a far comparire il pezzo sulla
            scheda della sede.
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Possesso</label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("tipo_possesso")}
          >
            <option value="">— Non specificato —</option>
            {(Object.keys(TIPO_POSSESSO_LABELS) as TipoPossessoDevice[]).map((p) => (
              <option key={p} value={p}>
                {TIPO_POSSESSO_LABELS[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Installato da Evo</label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("installato_da_evo")}
          >
            {/* Tre voci e non una spunta: "non so" è una risposta diversa da
                "no", e su questo campo la differenza ha una conseguenza —
                un "sì" con una data crea da solo la riga nel Calendario
                Installazioni. */}
            <option value="">— Non dichiarato —</option>
            <option value="si">Sì</option>
            <option value="no">No</option>
          </select>
          {watch("installato_da_evo") === "si" && (
            <p className="text-xs text-ink/50">
              Con anche la data di installazione, l'installazione corrispondente viene creata nel Calendario.
            </p>
          )}
        </div>

        <Input label="Data installazione" type="date" {...register("data_installazione")} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note</label>
          <textarea
            rows={3}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("note")}
          />
        </div>
      </form>
    </SlideOver>
  );
}
